import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
// import { environment } from '@env/environment';
import { GraphService } from '../services/graph-services/graph-service';
import { AppConfigService } from './appconfig.service';

@Injectable({
    providedIn: 'root',
})
export class AuthGuard implements CanActivate {
    private redirectInProgress = false;
    private readonly GROUP_MEMBERSHIP_KEY = 'group_membership_verified';
    private readonly GROUP_MEMBERSHIP_GROUP_ID_KEY = 'group_membership_group_id';
    private readonly GROUP_MEMBERSHIP_EXPIRES_ON_KEY = 'group_membership_expires_on';
    private readonly GROUP_MEMBERSHIP_TTL_MS = 5 * 60 * 1000;

    constructor(
        private readonly router: Router,
        private readonly msalService: MsalService,
        private readonly config: AppConfigService,
        private readonly graphService: GraphService,
    ) {
        this.config.getConfig('apiUrl');
    }

    async canActivate(_route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
        if (state.url.startsWith('/unauthorized')) {
            return true;
        }

        if (!this.isMsalEnabled()) {
            return true;
        }

        try {
            const instance = this.msalService.instance;
            if (!instance) {
                console.warn('[AuthGuard] MSAL instance not available');
                return true;
            }

            const account = this.getOrSetActiveAccount(instance);

            if (account) {
                await this.acquireAndStoreToken(account);

                if (!(await this.checkGroupMembership())) {
                    this.redirectToUnauthorized();
                    return false;
                }

                return true;
            }

            this.loginRedirect(state.url);
            return false;
        } catch (error) {
            console.error('[AuthGuard] Authentication error:', error);
            this.redirectToUnauthorized();
            return false;
        }
    }

    private getOrSetActiveAccount(instance: any): any {
        let account = instance.getActiveAccount();
        if (!account) {
            const accounts = instance.getAllAccounts();
            if (accounts.length > 0) {
                account = accounts[0];
                instance.setActiveAccount(account);
            }
        }
        return account;
    }

    private isMsalEnabled(): boolean {
        const enabled = this.config.getConfig('msalEnabled');
        const legacyEnabled = this.config.getConfig('msalEnable');
        return Boolean(enabled ?? legacyEnabled);
    }

    private loginRedirect(returnUrl: string): void {
        if (this.redirectInProgress) return;

        try {
            this.redirectInProgress = true;
            this.msalService.loginRedirect({
                scopes: [`${this.config.getConfig('clientId')}/.default`],
                redirectStartPage: returnUrl,
            });
        } catch (error) {
            this.redirectInProgress = false;
            console.error('[AuthGuard] Error redirecting to login:', error);
        }
    }

    /** Acquire token and store in sessionStorage. Try MSAL refresh before clearing on error. */
    private async acquireAndStoreToken(account: any): Promise<void> {
        if (this.hasValidStoredToken()) return;

        const scopes = [`${this.config.getConfig('clientId')}/.default`];

        try {
            const result = await this.msalService.instance.acquireTokenSilent({ account, scopes });
            this.storeToken(result.accessToken, result.expiresOn);
        } catch (err: any) {
            console.warn('[AuthGuard] Silent token acquisition failed', err);

            // Try force refresh before clearing and re-login
            if (this.isAuthError(err)) {
                try {
                    const result = await this.msalService.instance.acquireTokenSilent({
                        account,
                        scopes,
                        forceRefresh: true
                    });
                    this.storeToken(result.accessToken, result.expiresOn);
                } catch {
                    this.clearAllAndRelogin(scopes);
                }
            }
        }
    }

    private hasValidStoredToken(): boolean {
        const token = sessionStorage.getItem('access_token');
        const expiresOn = sessionStorage.getItem('access_token_expires_on');
        return !!(token && expiresOn && new Date(expiresOn).getTime() > Date.now());
    }

    private storeToken(accessToken: string, expiresOn?: Date | null): void {
        sessionStorage.setItem('access_token', accessToken);
        if (expiresOn) {
            sessionStorage.setItem('access_token_expires_on', expiresOn.toISOString());
        }
    }

    private isAuthError(err: any): boolean {
        return err?.errorCode === 'block_iframe_reload' ||
            err?.errorCode === 'token_expired' ||
            err?.errorCode === 'interaction_required' ||
            err?.name === 'BrowserAuthError' ||
            err?.name === 'InteractionRequiredAuthError';
    }

    private clearAllAndRelogin(scopes: string[]): void {
        sessionStorage.removeItem('access_token');
        sessionStorage.removeItem('access_token_expires_on');
        sessionStorage.removeItem(this.GROUP_MEMBERSHIP_KEY);
        sessionStorage.removeItem(this.GROUP_MEMBERSHIP_GROUP_ID_KEY);
        sessionStorage.removeItem(this.GROUP_MEMBERSHIP_EXPIRES_ON_KEY);

        try {
            this.msalService.loginRedirect({
                scopes,
                redirectStartPage: window.location.pathname,
            });
        } catch (error) {
            console.error('[AuthGuard] Failed to trigger MSAL login redirect:', error);
        }
    }

    private async checkGroupMembership(): Promise<boolean> {
        const requiredGroupId = this.config.getConfig('requiredGroupId');
        if (!requiredGroupId) return true;

        const cachedMembership = sessionStorage.getItem(this.GROUP_MEMBERSHIP_KEY);
        const cachedGroupId = sessionStorage.getItem(this.GROUP_MEMBERSHIP_GROUP_ID_KEY);
        const cachedExpiresOn = sessionStorage.getItem(this.GROUP_MEMBERSHIP_EXPIRES_ON_KEY);
        const cachedExpiryTime = cachedExpiresOn ? new Date(cachedExpiresOn).getTime() : NaN;

        if (
            cachedMembership !== null &&
            cachedGroupId === requiredGroupId &&
            Number.isFinite(cachedExpiryTime) &&
            cachedExpiryTime > Date.now()
        ) {
            return cachedMembership === 'true';
        }

        sessionStorage.removeItem(this.GROUP_MEMBERSHIP_KEY);
        sessionStorage.removeItem(this.GROUP_MEMBERSHIP_GROUP_ID_KEY);
        sessionStorage.removeItem(this.GROUP_MEMBERSHIP_EXPIRES_ON_KEY);

        try {
            const isMember = await this.graphService.isUserInRequiredGroup(requiredGroupId);

            sessionStorage.setItem(this.GROUP_MEMBERSHIP_KEY, String(isMember));
            sessionStorage.setItem(this.GROUP_MEMBERSHIP_GROUP_ID_KEY, requiredGroupId);
            sessionStorage.setItem(
                this.GROUP_MEMBERSHIP_EXPIRES_ON_KEY,
                new Date(Date.now() + this.GROUP_MEMBERSHIP_TTL_MS).toISOString(),
            );

            return isMember;
        } catch (error) {
            console.error('[AuthGuard] Error checking group membership:', error);
            sessionStorage.removeItem(this.GROUP_MEMBERSHIP_KEY);
            sessionStorage.removeItem(this.GROUP_MEMBERSHIP_GROUP_ID_KEY);
            sessionStorage.removeItem(this.GROUP_MEMBERSHIP_EXPIRES_ON_KEY);
            return false;
        }
    }

    private redirectToUnauthorized(): void {
        if (!this.router.url.startsWith('/unauthorized')) {
            this.router.navigate(['/unauthorized']);
        }
    }
}
